import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck2, Check, Calculator } from "lucide-react";
import { motion } from "framer-motion";
import { useDemo } from "@/components/public/DemoDialog";
import { LivePipeline } from "@/components/public/home/LivePipeline";

export function Hero() {
  const { openDemo } = useDemo();
  return (
    <section className="relative -mt-16 pt-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 rs-grid-bg [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 lg:pt-24 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <motion.div className="lg:col-span-6 min-w-0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <a href="#platform" className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border border-slate-200 bg-white/80 text-xs text-slate-700 hover:border-indigo-300 transition-colors" data-testid="hero-badge">
            <span className="px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-semibold">NEW</span> AI voice · video · coding interviews <ArrowRight className="w-3 h-3" />
          </a>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.04]">
            Hire faster.<br /><span className="bg-gradient-to-r from-indigo-600 via-indigo-500 to-cyan-500 bg-clip-text text-transparent">Interview smarter.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            RecruitSift runs first-round voice, video and coding interviews autonomously — then delivers a ranked shortlist with a scorecard for every candidate.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={openDemo} data-testid="hero-btn-book-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 hover:bg-indigo-600 text-white font-semibold transition-colors">
              <CalendarCheck2 className="w-4 h-4" /> Book a Demo
            </button>
            <Link to="/register" data-testid="hero-btn-start-trial" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 hover:border-slate-900 bg-white text-slate-900 font-semibold transition-colors">
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <a href="#roi" onClick={(e) => { e.preventDefault(); document.getElementById("roi")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            data-testid="hero-link-roi" className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
            <Calculator className="w-4 h-4" /> Calculate your ROI <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
            {["14-day free trial", "No credit card", "Live in under an hour"].map((t) => <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />{t}</span>)}
          </div>
        </motion.div>
        <motion.div className="lg:col-span-6 lg:pl-6 min-w-0" initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <LivePipeline />
        </motion.div>
      </div>
    </section>
  );
}
